import React from 'react';
import PropTypes from 'prop-types';
import { Badge, Button, Card, Col, OverlayTrigger, Row, Tooltip } from 'react-bootstrap';
import { icon } from '@fortawesome/fontawesome-svg-core/import.macro';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheckCircle } from '@fortawesome/free-solid-svg-icons';

/**
 * Componente para mostrar un gasto individual como tarjeta
 */
const GastoCard = ({ 
    gasto, 
    onEdit, 
    onCopy, 
    onView,
    getFormaPagoIcon,
    onDescargarNota,
    isSelected = false,
    onToggleSelection
}) => {
    const formattedDate = new Date(gasto.dFechaGasto).toLocaleDateString("es-MX", {
        day: "2-digit",
        month: "short",
        year: "numeric"
    }).replace(/(?:^|\s)([a-z])/g, char => char.toUpperCase());

    const currency = gasto.vMoneda === 'USD' ? 'USD' : 'MXN';

    /**
     * Manejador de clic en la tarjeta
     */
    const handleCardClick = (e) => {
        // No activar selecci�n si se hace clic en un bot�n o enlace
        if (e.target.tagName === 'A' || e.target.closest('a') || e.target.closest('button')) {
            return;
        }

        if (onToggleSelection) {
            onToggleSelection(gasto.idGasto);
        }
    };

    // Determinar clases CSS seg�n estado de selecci�n
    const cardClasses = [
        'mb-3',
        'shadow-sm',
        isSelected ? 'border-primary border-3' : ''
    ].filter(Boolean).join(' ');

    // Estilos din�micos para el fondo compatible con modo oscuro
    const cardStyle = {
        cursor: 'pointer',
        transition: 'all 0.2s ease-in-out',
        ...(isSelected && {
            backgroundColor: 'rgba(13, 110, 253, 0.1)',
            boxShadow: '0 0 0 0.2rem rgba(13, 110, 253, 0.25)'
        })
    };

    // compute equivalent in MXN when currency not MXN
    const computeEquivalentMXN = () => {
        try {
            if (!gasto || gasto.vMoneda === 'MXN') return null;
            const total = gasto.dTotal != null ? parseFloat(gasto.dTotal) : NaN;
            const rate = gasto.dTipoCambio != null ? parseFloat(gasto.dTipoCambio) : NaN;
            if (Number.isNaN(total) || Number.isNaN(rate)) return null;
            const eq = total * rate;
            if (!Number.isFinite(eq)) return null;
            return eq;
        } catch {
            return null;
        }
    };

    const equivalentMXN = computeEquivalentMXN();

    return (
        <Card 
            className={cardClasses}
            onClick={handleCardClick}
            style={cardStyle}
        >
            <Card.Body>
                <Row className="align-items-center">
                    {/* ID y acciones */}
                    <Col xs={12} md={2} className="mb-2 mb-md-0">
                        <div className="d-flex align-items-center justify-content-between">
                            <div>
                                <div className="d-flex align-items-center gap-2 mb-1">
                                    {isSelected && (
                                        <FontAwesomeIcon
                                            icon={faCheckCircle}
                                            className="text-primary"
                                            size="lg"
                                        />
                                    )}
                                    <h6 className="mb-0">
                                        <span
                                            style={{
                                                color: '#2c7be5',
                                                cursor: 'pointer',
                                                fontWeight: '600'
                                            }}
                                            onClick={() => onView(gasto.idGasto)}
                                        >
                                            #{gasto.idGasto}
                                        </span>
                                    </h6>
                                </div>
                                <small><b>{formattedDate}</b></small>
                                <div className="d-flex gap-2 mt-2">
                                    <OverlayTrigger placement="top" overlay={<Tooltip>Copiar gasto</Tooltip>}>
                                        <Button
                                            variant="outline-info"
                                            size="sm"
                                            onClick={() => onCopy(gasto.idGasto)}
                                        >
                                            <FontAwesomeIcon icon={icon({ name: 'copy', style: "solid" })} />
                                        </Button>
                                    </OverlayTrigger>
                                    <OverlayTrigger placement="top" overlay={<Tooltip>Editar gasto</Tooltip>}>
                                        <Button
                                            variant="outline-primary"
                                            size="sm"
                                            onClick={() => onEdit(gasto.idGasto)}
                                        >
                                            <FontAwesomeIcon icon={icon({ name: 'edit', style: "solid" })} />
                                        </Button>
                                    </OverlayTrigger>
                                </div>
                            </div>
                        </div>
                    </Col>

                    {/* Informaci&oacute;n principal */}
                    <Col xs={12} md={6} className="mb-2 mb-md-0">
                        <div className="mb-2">
                            <h6 className="mb-1" title={gasto.vDescripcion}>
                                {gasto.dCantidad && (
                                    <>
                                        (<FontAwesomeIcon icon={icon({ name: 'hashtag', style: "solid" })} className="me-1" />
                                        {parseFloat(gasto.dCantidad).toLocaleString("es-MX", {
                                            minimumFractionDigits: 0,
                                            maximumFractionDigits: 2
                                        })} ) &nbsp; 
                                    </>
                                )}
                                <b>{gasto.vDescripcion}</b>
                            </h6>
                            <div className="d-flex flex-wrap gap-2 align-items-center">
                                <div className="d-flex align-items-center gap-1">
                                    {getFormaPagoIcon(gasto.vFormaPago)}
                                    <small>{gasto.vFormaPago}</small>
                                </div>
                                
                                {gasto.vClaveUnidad && (
                                    <Badge bg="light" text="dark" className="fs--2">
                                        Unidad: {gasto.vClaveUnidad}
                                    </Badge>
                                )}
                                {gasto.vCodigoSat && (
                                    <Badge bg="light" text="dark" className="fs--2">
                                        SAT: {gasto.vCodigoSat}
                                    </Badge>
                                )}
                            </div>
                        </div>
                        <div className="small">
                            <strong>{gasto.vTipoGasto}</strong>
                        </div>
                        <div className="text-muted small">
                            <strong>Responsable:</strong> {gasto.vResponsable}
                        </div>
                       
                        {gasto.DetallesCuenta && (
                            <div className="mt-1">
                                <Badge bg="light" text="dark" className="fs--2">
                                    Cuenta: {gasto.DetallesCuenta}
                                </Badge>
                            </div>
                        )}
                        {(gasto.vRfcEmisor || gasto.vRazonSocialEmisor) && (
                            <div className="text-muted small">
                                <strong>Emisor:</strong> {gasto.vRfcEmisor}{gasto.vRfcEmisor && gasto.vRazonSocialEmisor && ' - '}{gasto.vRazonSocialEmisor}
                            </div>
                        )}
                        {gasto.vUUIDFactura && gasto.idFacturaRecibida > 0 && (
                            <div className="mt-2">
                                <small className="text-muted d-block mb-1">
                                    <strong>
                                        <FontAwesomeIcon icon={icon({ name: 'file-invoice', style: "solid" })} className="me-1" />
                                        Factura:
                                    </strong>
                                </small>
                                <a
                                    href={`/contabilidad/centro-xml/facturas-recibidas/visualizacion/${gasto.idFacturaRecibida}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-primary"
                                    style={{
                                        fontSize: '0.85rem',
                                        fontFamily: 'monospace',
                                        wordBreak: 'break-all',
                                        textDecoration: 'none'
                                    }}
                                    title="Ver factura recibida"
                                >
                                    {gasto.vUUIDFactura}
                                </a>
                            </div>
                        )}
                        {gasto.bHasNotaVenta === 1 && (
                            <div className="mt-1">
                                <Button
                                    variant="outline-primary"
                                    size="sm"
                                    onClick={() => onDescargarNota(gasto.idGasto)}
                                    title="Descargar nota de venta"
                                >
                                    <FontAwesomeIcon icon={icon({ name: 'download', style: "solid" })} className="me-1" />
                                    Ver Nota de Venta
                                </Button>
                            </div>
                        )}
                    </Col>

                    {/* Informaci&oacute;n financiera */}
                    <Col xs={12} md={4} className="text-end">
                        <div className="mb-2">
                            <h5 className=" mb-1">
                                {gasto.dTotal.toLocaleString("es-MX", {
                                    style: "currency",
                                    currency: currency,
                                    minimumFractionDigits: 2,
                                    maximumFractionDigits: 2
                                })}
                            </h5>
                            <div className="d-flex justify-content-end align-items-center gap-2 mb-2">
                                <Badge
                                    bg={gasto.vMoneda === 'MXN' ? 'primary' : 'warning'}
                                    className="fs--2"
                                >
                                    {gasto.vMoneda}
                                </Badge>
                                {gasto.dTipoCambio != 1 && (
                                    <small className="text-muted">
                                        T.C. {parseFloat(gasto.dTipoCambio).toFixed(4)}
                                    </small>
                                )}
                            </div>
                            
                            {/* Detalles financieros */}
                            <div className="small">
                                {gasto.dImporte !== undefined && gasto.dImporte !== null && (
                                    <div className="d-flex justify-content-between mb-1">
                                        <span>Importe:</span>
                                        <span className="fw-semibold">
                                            {parseFloat(gasto.dImporte).toLocaleString("es-MX", {
                                                style: "currency",
                                                currency: currency,
                                                minimumFractionDigits: 2,
                                                maximumFractionDigits: 2
                                            })}
                                        </span>
                                    </div>
                                )}
                                {gasto.dDescuento !== undefined && gasto.dDescuento !== null && gasto.dDescuento > 0 && (
                                    <div className="d-flex justify-content-between mb-1">
                                        <span>Descuento:</span>
                                        <span className="fw-semibold text-danger">
                                            -{parseFloat(gasto.dDescuento).toLocaleString("es-MX", {
                                                style: "currency",
                                                currency: currency,
                                                minimumFractionDigits: 2,
                                                maximumFractionDigits: 2
                                            })}
                                        </span>
                                    </div>
                                )}
                                {gasto.dIva !== undefined && gasto.dIva !== null && gasto.dIva > 0 && (
                                    <div className="d-flex justify-content-between mb-1">
                                        <span>IVA:</span>
                                        <span className="fw-semibold">
                                            {parseFloat(gasto.dIva).toLocaleString("es-MX", {
                                                style: "currency",
                                                currency: currency,
                                                minimumFractionDigits: 2,
                                                maximumFractionDigits: 2
                                            })}
                                        </span>
                                    </div>
                                )}
                                {gasto.dIvaRet !== undefined && gasto.dIvaRet !== null && gasto.dIvaRet > 0 && (
                                    <div className="d-flex justify-content-between mb-1">
                                        <span>IVA Ret.:</span>
                                        <span className="fw-semibold text-warning">
                                            -{parseFloat(gasto.dIvaRet).toLocaleString("es-MX", {
                                                style: "currency",
                                                currency: currency,
                                                minimumFractionDigits: 2,
                                                maximumFractionDigits: 2
                                            })}
                                        </span>
                                    </div>
                                )}
                                {gasto.dIsrRet !== undefined && gasto.dIsrRet !== null && gasto.dIsrRet > 0 && (
                                    <div className="d-flex justify-content-between mb-1">
                                        <span>ISR Ret.:</span>
                                        <span className="fw-semibold text-warning">
                                            -{parseFloat(gasto.dIsrRet).toLocaleString("es-MX", {
                                                style: "currency",
                                                currency: currency,
                                                minimumFractionDigits: 2,
                                                maximumFractionDigits: 2
                                            })}
                                        </span>
                                    </div>
                                )}
                            </div>
                        </div>
                        {equivalentMXN !== null && (
                            <div className="text-end mt-1">
                            <hr></hr>
                                <small className="text-primary">{equivalentMXN.toLocaleString("es-MX", { style: "currency", currency: "MXN" })}  MXN</small>
                            </div>
                        )}
                    </Col>
                </Row>
            </Card.Body>
        </Card>
    );
};

GastoCard.propTypes = {
    gasto: PropTypes.shape({
        idGasto: PropTypes.number.isRequired,
        dFechaGasto: PropTypes.string.isRequired,
        vMoneda: PropTypes.string.isRequired,
        dTotal: PropTypes.number.isRequired,
        vDescripcion: PropTypes.string.isRequired,
        dCantidad: PropTypes.number,
        dIva: PropTypes.number,
        dIvaRet: PropTypes.number,
        dIsrRet: PropTypes.number,
        dDescuento: PropTypes.number,
        dImporte: PropTypes.number,
        vFormaPago: PropTypes.string,
        vClaveUnidad: PropTypes.string,
        vCodigoSat: PropTypes.string,
        vResponsable: PropTypes.string,
        vTipoGasto: PropTypes.string,
        idFacturaRecibida: PropTypes.number,
        bHasNotaVenta: PropTypes.number,
        dTipoCambio: PropTypes.number,
        DetallesCuenta: PropTypes.string,
        vRfcEmisor: PropTypes.string,
        vRazonSocialEmisor: PropTypes.string,
        vUUIDFactura: PropTypes.string
    }).isRequired,
    onEdit: PropTypes.func.isRequired,
    onCopy: PropTypes.func.isRequired,
    onView: PropTypes.func.isRequired,
    getFormaPagoIcon: PropTypes.func.isRequired,
    onDescargarNota: PropTypes.func.isRequired,
    isSelected: PropTypes.bool,
    onToggleSelection: PropTypes.func
};

export default GastoCard;
